import { Button } from "../ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/card";
import { Label } from "../ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Badge } from "../ui/badge";
import { formatTitleCase } from "../../lib/formatting";

type QueueOption = {
  value: string;
  label: string;
};

type ReviewQueueFiltersProps = {
  slaFilter: "all" | "breached";
  queueFilter: string;
  queueOptions: QueueOption[];
  onSlaChange: (value: "all" | "breached") => void;
  onQueueChange: (value: string) => void;
};

export function ReviewQueueFilters({
  slaFilter,
  queueFilter,
  queueOptions,
  onSlaChange,
  onQueueChange,
}: ReviewQueueFiltersProps) {
  return (
    <>
      <Card className="border-border bg-card/60">
        <CardHeader className="space-y-1">
          <CardTitle className="text-lg">Filters</CardTitle>
          <CardDescription>
            Focus on items breaching SLA or assigned to a specific queue.
          </CardDescription>
        </CardHeader>
        <CardContent className="grid gap-4 md:grid-cols-3">
          <div className="space-y-2">
            <Label htmlFor="sla-filter">SLA status</Label>
            <Select
              value={slaFilter}
              onValueChange={(value) => onSlaChange(value as "all" | "breached")}
            >
              <SelectTrigger id="sla-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="breached">Breached</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="queue-filter">Queue</Label>
            <Select
              value={queueFilter}
              onValueChange={(value) => onQueueChange(value)}
            >
              <SelectTrigger id="queue-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {queueOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>
      {(slaFilter !== "all" || queueFilter !== "all") ? (
        <div className="flex flex-wrap items-center gap-2 rounded-md border border-border bg-card/60 px-3 py-2 text-xs text-muted-foreground">
          <span className="uppercase tracking-[0.2em] text-muted-foreground/70">
            Active filters
          </span>
          {slaFilter !== "all" ? (
            <button
              type="button"
              onClick={() => onSlaChange("all")}
              aria-label="Clear SLA filter"
            >
              <Badge variant="outline" className="text-foreground/90">
                SLA: {formatTitleCase(slaFilter)} ✕
              </Badge>
            </button>
          ) : null}
          {queueFilter !== "all" ? (
            <button
              type="button"
              onClick={() => onQueueChange("all")}
              aria-label="Clear queue filter"
            >
              <Badge variant="outline" className="text-foreground/90">
                {
                  queueOptions.find((option) => option.value === queueFilter)
                    ?.label ?? formatTitleCase(queueFilter)
                }{" "}
                ✕
              </Badge>
            </button>
          ) : null}
          <Button
            variant="ghost"
            className="ml-auto h-7 px-2 text-xs"
            onClick={() => {
              onSlaChange("all");
              onQueueChange("all");
            }}
          >
            Reset all
          </Button>
        </div>
      ) : null}
    </>
  );
}
